import { createMuiTheme } from '@material-ui/core/styles'
import { fontTypes as Effra } from '~/assets/fonts/effra/effra.exports'
import { fontTypes as DomaineSansText } from '~/assets/fonts/domaine-sans-text/domaine-sans-text.exports'

const titleFonts = [
  '"Domaine Sans Text"',
  'Times new roman',
  'Serif'
].join(', ')

const palette = {
  inverse: false,
  background: {
    paper: '#FAFAFA',
    default: '#ffffff'
  },
  // Custom palette customized for brand
  primary: {
    main: '#007E78'
  },
  secondary: {
    main: '#499D71',
    contrast: '#008626'
  },
  error: {
    main: '#DE1616'
  },
  warning: {
    main: '#FF9700'
  },
  info: {
    main: '#1074AB'
  },
  success: {
    main: '#23B218'
  },
  'status-alert': {
    main: '#FBC679'
  },
  // Extra palette customized for brand
  'brand-primary': {
    main: '#007E78'
  },
  'brand-secondary': {
    main: '#65987C'
  },
  'brand-tertiary': {
    main: '#A9CB7A'
  },
  'neutral-ultra-dark': {
    main: '#000000'
  },
  'neutral-dark': {
    main: '#515151'
  },
  'neutral-medium': {
    main: '#878787'
  },
  'neutral-light': {
    main: '#F5F5F5'
  },
  'neutral-ultra-light': {
    main: '#FFFFFF'
  },
  'action-conversion-static': {
    main: '#384ECA'
  },
  'action-conversion-hover': {
    main: '#304873'
  },
  'action-conversion-disabled': {
    main: '#D6D6D6'
  },
  'action-navigation-static': {
    main: '#499D71'
  },
  'action-navigation-hover': {
    main: '#37736C'
  },
  'action-navigation-disabled': {
    main: '#D6D6D6'
  },
  'action-contrast-static': {
    main: '#FFFFFF',
    contrastText: '#515151'
  },
  'action-contrast-hover': {
    main: '#2DD1FF'
  },
  'action-contrast-disabled': {
    main: '#ffffff3d',
    contrastText: '#D6D6D6'
  },
  // Suport palette
  'support-whatsapp': {
    main: '#128C7E'
  },
  'support-facebook': {
    main: '#3B5998'
  },
  'support-BOT': {
    main: '#6F967E',
    text: '#ffffff',
    name: 'Boticário'
  },
  'support-EUD': {
    main: '#472D75',
    text: '#ffffff',
    name: 'Eudora'
  },
  'support-QDB': {
    main: '#DB5958',
    text: '#ffffff',
    name: 'Quem Disse Berenice'
  },
  'support-BLZ': {
    main: '#6A1B60',
    text: '#ffffff',
    name: 'Beleza na Web'
  },
  contrastThreshold: 3,
  tonalOffset: 0.2,
  date: {
    color: '#499D71',
    name: 'verde'
  }
}

const theme = createMuiTheme()

export default createMuiTheme({
  palette,
  spacing: (i) => (i < 5 ? i * 4 : ((i - 2) * 8)),
  breakpoints: {
    values: {
      xs: 0,
      sm: 425,
      md: 1024,
      lg: 1440,
      xl: 1920,
      mobile: 425,
      tablet: 1024,
      desktop: 1440
    }
  },
  typography: {
    fontFamily: [
      '"Effra"',
      'Helvetica',
      'Arial',
      'sans-serif'
    ].join(', '),
    fontFeatureSettings: '"liga" off',
    fontWeight: 400,
    fontStyle: 'normal',
    h1: {
      fontFamily: titleFonts,
      fontSize: '4rem',
      lineHeight: '4.5rem',
      fontWeight: 600
    },
    h2: {
      fontFamily: titleFonts,
      fontSize: '3.5rem',
      lineHeight: '4rem',
      fontWeight: 600
    },
    h3: {
      fontFamily: titleFonts,
      fontSize: '3rem',
      lineHeight: '4rem',
      fontWeight: 600
    },
    h4: {
      fontFamily: titleFonts,
      fontSize: '2.5rem',
      lineHeight: '3rem',
      fontWeight: 600
    },
    h5: {
      fontSize: '2rem',
      lineHeight: '2.5rem'
    },
    h6: {
      fontSize: '1.4rem',
      lineHeight: '1.7rem'
    },
    body1: {
      fontSize: '1.2rem',
      lineHeight: '1.5rem'
    },
    body2: {
      fontSize: '1.1rem',
      lineHeight: '1.4rem'
    },
    subtitle1: {
      fontSize: '1.2rem',
      lineHeight: '1.3rem'
    },
    subtitle2: {
      fontSize: '1.1rem',
      lineHeight: '1.2rem'
    },
    caption: {
      fontSize: '1rem',
      lineHeight: '1.3rem'
    },
    overline: {
      fontSize: '0.9rem',
      lineHeight: '1.1rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em'
    }
  },
  overrides: {
    MuiDrawer: {
      paper: {
        width: 360,
        backgroundColor: palette['neutral-light'].main
      },
      paperAnchorDockedLeft: {
        position: 'relative'
      }
    },
    MuiButton: {
      root: {
        borderRadius: 4,
        height: 48
      },
      contained: {
        width: '100%',
        boxShadow: 'none',
        '&:not(.Mui-disabled)': {
          color: palette['neutral-ultra-light'].main
        }
      },
      sizeSmall: {
        height: 32
      },
      sizeLarge: {
        height: 64
      }
    },
    MuiDivider: {
      light: {
        backgroundColor: palette['neutral-ultra-light'].main
      }
    },
    MuiTableCell: {
      head: {
        fontStyle: 'normal',
        fontSize: 10,
        lineHeight: '16px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 600
      },
      body: {
        fontStyle: 'normal',
        fontSize: 14,
        lineHeight: '24px',
        fontWeight: 400
      }
    },
    MuiListItem: {
      root: {
        paddingTop: 10,
        paddingBottom: 10,
        '&$selected': {
          backgroundColor: palette['neutral-dark'].main,
          color: palette['neutral-ultra-light'].main,
          '&:hover': {
            backgroundColor: '#5f5d5d'
          },
          '& .MuiListItemIcon-root': {
            color: palette['neutral-ultra-light'].main
          }
        }
      }
    },
    MuiRadio: {
      root: {
        '& .MuiSvgIcon-root': {
          fontSize: '1rem'
        }
      }
    },
    MuiTextField: {
      root: {
        '& .MuiInputBase-root': {
          backgroundColor: palette['neutral-ultra-light'].main
        }
      }
    },
    MuiInputBase: {
      root: {
        transition: 'all 0.3s ease-in-out',
        '&.Mui-focused': {
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.32)'
        }
      },
      input: {
        '&:-webkit-autofill': {
          '-webkitBackgroundClip': 'text'
        }
      }
    },
    MuiOutlinedInput: {
      input: {
        padding: '0 12px',
        minHeight: 48
      }
    },
    MuiFormHelperText: {
      root: {
        textTransform: 'normal'
      }
    },
    MuiSelect: {
      selectMenu: {
        padding: '0 14px',
        minHeight: 48
      },
      select: {
        display: 'flex',
        alignItems: 'center'
      }
    },
    MuiFormLabel: {
      root: {
        lineHeight: '7px'
      }
    },
    MuiListItemIcon: {
      root: {
        justifyContent: 'center',
        minWidth: 40
      }
    },
    MuiListItemAvatar: {
      root: {
        minWidth: 32
      }
    },
    MuiAvatar: {
      square: {
        width: 24,
        height: 24
      }
    },
    MuiAlert: {
      root: {
        '& .MuiAlert-icon': {
          alignItems: 'center'
        }
      },
      standardError: {
        backgroundColor: 'transparent',
        paddingLeft: 0,
        paddingRight: 0,
        color: palette.error.main
      },
      filledSuccess: {
        backgroundColor: palette.secondary.main
      }
    },
    MuiTabs: {
      root: {
        minHeight: 32,
        '& .MuiTab-root:not(.Mui-selected) .MuiTab-wrapper': {
          borderBottom: 'solid 1px #878787'
        },
        '& .MuiTabScrollButton-root': {
          width: 24
        },
        '& .MuiTab-wrapper': {
          flexDirection: 'row',
          paddingBottom: 6,
          '& .MuiSvgIcon-root': {
            margin: '0 10px 0 0'
          }
        },
        '& .MuiTab-root': {
          minWidth: 'initial',
          minHeight: 32,
          padding: '0 16px 4px'
        },
        '& .MuiTabs-indicator': {
          height: 4,
          bottom: 3
        }
      }
    },
    MuiPaper: {
      root: {
        '&.MuiPaper-elevation1': {
          boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.15)'
        }
      }
    },
    MuiPickersBasePicker: {
      container: {
        alignItems: 'center'
      }
    },
    MuiPopover: {
      root: {
        zIndex: '999999 !important',
        '& .MuiMenuItem-root': {
          minHeight: '48px !important'
        },
        '& .MuiListItem-root.Mui-selected': {
          backgroundColor: 'transparent',
          color: '#8DC63F',
          paddingLeft: 24
        }
      }
    },
    MuiTooltip: {
      tooltip: {
        color: '#ffffff',
        backgroundColor: palette.warning.main,
        fontStyle: 'normal',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        padding: 16
      },
      arrow: {
        color: palette.warning.main
      }
    },
    MuiCssBaseline: {
      '@global': {
        html: {
          fontSize: '100%',
          [theme.breakpoints.down('lg')]: {
            fontSize: '95%'
          },
          [theme.breakpoints.down('md')]: {
            fontSize: '85%'
          },
          [theme.breakpoints.down('sm')]: {
            fontSize: '80%'
          }
        },
        a: {
          color: palette['action-conversion-static'].main,
          '&:hover': {
            color: palette['action-conversion-hover'].main
          }
        },
        '@font-face': [...Effra, ...DomaineSansText],
        '*::-webkit-scrollbar': {
          width: '0.4em'
        },
        '*::-webkit-scrollbar-track': {
          '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)'
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,.4)',
          outline: '1px solid slategrey',
          borderRadius: 3
        },
        '#root': {
          width: '100%'
        },
        '.auxiliar': {
          fontSize: 12,
          lineHeight: '16px'
        },
        '.colorNeutralUltraDark': {
          color: palette['neutral-ultra-dark'].main
        },
        '.colorNeutralDark': {
          color: palette['neutral-dark'].main
        },
        '.colorNeutralMedium': {
          color: palette['neutral-medium'].main
        },
        '.colorNeutralLight': {
          color: palette['neutral-light'].main
        },
        '.boxCenter': {
          alignItems: 'center',
          display: 'flex',
          flex: 1
        }
      }
    }
  }
})
